import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  ViewEncapsulation,
} from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup
} from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import {
  filter,
  map,
  startWith,
  switchMap,
  take,
  tap
} from 'rxjs/operators';
import {
  Address,
  Title,
} from '@spartacus/core';
import {
  B2BUnitNode,
  OrgUnitService,
} from '@spartacus/organization/administration/core';
import {
  CurrentItemService,
  CurrentUserService,
  FormComponent,
  ItemService,
  MessageService,
} from '@spartacus/organization/administration/components';
import { UigUserItemService } from '../../services/uig-user-item/uig-user-item.service';
import { UigB2BUserService } from '../../services/uig-b2b-user/uig-b2b-user.service';
import { UigAccessSubjectService } from '../../services/uig-access-subject/uig-access-subject.service';
import {
  UigB2BUser,
  UigUserForm
} from '../uig-user.model';
import {
  AccessSubject,
  AccessSubjectList,
  AllowedPermission,
  AllowedPermissionOrMouseEvent,
  NormalizedAccessSubject
} from '../../store/uig-access-subject/uig-access-subject.model';
import { UigDeliveryDetailsService } from 'src/app/services/uig-delivery-details.service';
import { DeliveryData } from '../../uig-organization.model';
import {UserProfileFacade} from '@spartacus/user/profile/root';
import {DeliveryMode} from '@spartacus/cart/base/root';

@Component({
    selector: 'app-uig-user-form',
    templateUrl: './uig-user-form.component.html',
    styleUrls: ['./uig-user-form.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'content-wrapper' },
    providers: [
        {
            provide: ItemService,
            useExisting: UigUserItemService,
        },
        {
            provide: CurrentItemService,
            useExisting: CurrentUserService,
        },
    ],
    standalone: false
})
export class UigUserFormComponent extends FormComponent<UigUserForm> implements OnInit, OnDestroy {
  form: UntypedFormGroup = this.uigUserItemService.getForm();
  unitId: string;
  // Initialize the business unit for the user. If there's a unit provided, we disable the unit form control.
  @Input() set unitKey(value: string) {
    if (value) {
      this.form?.get('orgUnit.uid').setValue(value);
      this.form?.get('orgUnit')?.disable();
    }
  }

  units$: Observable<B2BUnitNode[]> = this.unitService.getActiveUnitList().pipe(
    tap((units) => {
      if (units.length === 1) {
        this.form?.get('orgUnit.uid').setValue(units[0]?.id);
        this.unitId = units[0]?.id;
      }
    })
  );

  titles$: Observable<Title[]> = this.userProfileFacade.getTitles();

  public userRoles: string[];
  public userId: string;
  edit: boolean = false;

  deliveryData$: Observable<DeliveryData>;
  deliveryDataSubscription = new Subscription();
  itemSubscription = new Subscription();
  currentSubscription = new Subscription();

  public deliveryAddresses: Address[];
  public deliveryModes: DeliveryMode[];

  public selectedDeliveryAddressId: string;
  public selectedDeliveryModeCode: string;

  accessSubjectOverviewData$: Observable<AccessSubjectList>;

  public accessSubjectOverviewData: AccessSubjectList;
  public accessSubjectArr: NormalizedAccessSubject[];

  constructor(
    protected uigUserItemService: UigUserItemService,
    @Optional() protected messageService: MessageService,
    protected unitService: OrgUnitService,
    protected userProfileFacade: UserProfileFacade,
    protected uigB2BUserService: UigB2BUserService,
    private uigDeliveryDetailsService: UigDeliveryDetailsService,
    private accessSubjectService: UigAccessSubjectService,
  ) {
    super(uigUserItemService, messageService);
  }

  ngOnInit(): void {
    this.createOrEdit();
    this.unitService.loadList();
    this.units$.subscribe();
    this.itemSubscription = this.uigUserItemService.key$.pipe(
      filter((code) => code != undefined),
      switchMap((code) => this.uigUserItemService.load(code)),
      tap((userDetails: UigB2BUser) => {
        this.userRoles = userDetails.roles
        this.userId = userDetails.uid;
        this.buildAccessSubjectArr(userDetails.roles);
        this.getDeliveryDetails(this.userId);
      }),
      startWith({})
    )
    .subscribe(() => {
      if (!this.userId)
      {
        this.getDeliveryDetails("");
      }
    });

    this.accessSubjectOverviewData$ = this.accessSubjectService.loadAccessSubjectOverview()
      .pipe(
        filter((accessSubjectOverviewData: AccessSubjectList) => accessSubjectOverviewData != null),
        tap((accessSubjectOverviewData: AccessSubjectList) => {
          this.accessSubjectOverviewData = accessSubjectOverviewData;
        })
      );
  }

  selectDeliveryMode(deliveryModeCode: string): void {
    this.selectedDeliveryModeCode = deliveryModeCode;
  }

  selectDeliveryAddress(deliveryAddressId: string): void {
    this.selectedDeliveryAddressId = deliveryAddressId;
  }

  updateRoles(event: AllowedPermissionOrMouseEvent, roleCode?: string) {
    if (event.target) {
      const { checked, value } = event.target as HTMLInputElement;
      if (checked) {
        this.roles.push(new UntypedFormControl(value));
      } else {
        this.roles.removeAt(this.roles.value.indexOf(value));
      }
    } else {
      let currentItemAllowedPermissions: AllowedPermission[] =
        this.accessSubjectOverviewData.ringfotoAccessSubjects.find(({ code }) => code === roleCode).allowedPermissions;

      currentItemAllowedPermissions.forEach((allowedPermission: AllowedPermission) => {
        if (this.roles.value.indexOf(allowedPermission.uid) !== -1) {
          this.roles.removeAt(this.roles.value.indexOf(allowedPermission.uid));
        }
      });

      this.roles.push(new UntypedFormControl(event.uid));
    }
  }

  private buildAccessSubjectArr(userDetailsRoles: string[]): void {
    let normalizedAccessSubjectsArr = [];

    userDetailsRoles.forEach((role: string) => {
      if (this.accessSubjectOverviewData?.ringfotoAccessSubjects) {
        const accessSubjects = this.accessSubjectOverviewData.ringfotoAccessSubjects;

        accessSubjects.forEach((accessSubject: AccessSubject) => {
          accessSubject.allowedPermissions.forEach((allowedPermission: AllowedPermission) => {
            if (allowedPermission) {
              if (role === allowedPermission.uid) {
                let normalizedAccessSubject: NormalizedAccessSubject = {
                  code: accessSubject.code,
                  name: accessSubject.name,
                  order: accessSubject.order,
                  multiplePermissions: accessSubject.multiplePermissions,
                  allowedPermissionCode: allowedPermission.uid,
                  allowedPermissionLabel: allowedPermission.label
                }

                let allowedPermissionIndex = normalizedAccessSubjectsArr.findIndex(x => x.allowedPermissionCode === allowedPermission.uid)

                if (allowedPermissionIndex === -1) {
                  normalizedAccessSubjectsArr.push(normalizedAccessSubject)
                }
              }
            }
          });
        });
      }
    });

    this.accessSubjectArr = normalizedAccessSubjectsArr.sort((a, b) => a.order - b.order);
  }


  public addCheckboxPlaceholder(allowedPermission: AllowedPermission): boolean {
    if (allowedPermission.label === 'bearbeiten') {
      return true;
    } else {
      return false;
    }
  }

  public getAllowedPermission(code: string): string {
    let matchingCodes: NormalizedAccessSubject[];

    if (this.accessSubjectArr) {
      matchingCodes = this.accessSubjectArr.filter((accessSubject: NormalizedAccessSubject) => {
        return accessSubject.code === code
      });
    }

    if (matchingCodes?.[0]) {
      return matchingCodes[0].allowedPermissionLabel;
    } else {
      return '';
    }
  }

  get roles(): UntypedFormArray {
    return this.form.get('roles') as UntypedFormArray;
  }

  getDeliveryDetails(userId: string) {
    this.deliveryData$ = this.uigDeliveryDetailsService.getDeliveryDetails(userId)
    .pipe(
      take(1),
      filter((deliveryData) => deliveryData != null),
      tap((deliveryData: DeliveryData) => {
        this.deliveryModes = deliveryData.deliveryModes;
        this.deliveryAddresses = deliveryData.deliveryAddresses;
        if(this.deliveryModes && this.deliveryModes.find((mode) => mode.selected))
        {
          this.selectDeliveryMode(
            this.deliveryModes.find((mode) => mode.selected).code
          );
        }
        if(this.deliveryAddresses && this.deliveryAddresses.find((address) => address.defaultAddress))
        {
          this.selectDeliveryAddress(
            this.deliveryAddresses.find((address) => address.defaultAddress).id
          );
        }
      })
    );
    this.deliveryDataSubscription = this.deliveryData$.subscribe();
  }

  createOrEdit() {
    this.uigUserItemService.current$
      .pipe(
        filter((item) => item != undefined),
        map((item) => {
        if(item) {
          this.edit = true;
        }
      }
      )).subscribe();
  }

  ngOnDestroy()
  {
    this.deliveryDataSubscription.unsubscribe();
    this.itemSubscription.unsubscribe();
    this.currentSubscription.unsubscribe();
  }
}
